const ADMIN_AUTH_TOKEN_KEY = 'admin_access_token';
const ADMIN_FALLBACK_USER_KEY = 'admin_fallback_user';

type TokenClaims = {
  username?: string;
  user_id?: string;
  admin_scope?: string;
  company_id?: string | number;
  company_key?: string;
  company_name?: string;
  mini_program_id?: string | number;
  admin_views?: string[];
  admin_region_id?: string | number;
  admin_region_name?: string;
  exp?: number;
};

const decodeBase64Url = (value: string) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');

  if (typeof window === 'undefined') {
    return Buffer.from(padded, 'base64').toString('utf-8');
  }

  return window.atob(padded);
};

export const buildFallbackCurrentUserFromToken = (token: string): API.CurrentUser | undefined => {
  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return undefined;
    }

    const claims = JSON.parse(decodeBase64Url(payload)) as TokenClaims;
    if (!claims.user_id && !claims.username) {
      return undefined;
    }

    const suffix = claims.username ? claims.username.slice(-8) : String(claims.user_id || '');

    return {
      userid: claims.user_id || '',
      name: `临时登录用户 ${suffix}`,
      access: 'admin',
      adminScope: claims.admin_scope || 'platform',
      admin_scope: claims.admin_scope || 'platform',
      adminViews: claims.admin_views || [],
      admin_views: claims.admin_views || [],
      adminRegionId:
        claims.admin_region_id === undefined
          ? undefined
          : String(claims.admin_region_id),
      admin_region_id: claims.admin_region_id,
      adminRegionName: claims.admin_region_name,
      admin_region_name: claims.admin_region_name,
      companyId: claims.company_id === undefined ? undefined : String(claims.company_id),
      company_id: claims.company_id,
      companyKey: claims.company_key,
      company_key: claims.company_key,
      companyName: claims.company_name,
      company_name: claims.company_name,
      miniProgramId:
        claims.mini_program_id === undefined ? undefined : String(claims.mini_program_id),
      mini_program_id: claims.mini_program_id,
    };
  } catch (_error) {
    return undefined;
  }
};

export const getAdminToken = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.localStorage.getItem(ADMIN_AUTH_TOKEN_KEY) || '';
};

export const setAdminToken = (token: string) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(ADMIN_AUTH_TOKEN_KEY, token);
};

export const getAdminFallbackUser = (): API.CurrentUser | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const raw = window.localStorage.getItem(ADMIN_FALLBACK_USER_KEY);
  if (!raw) {
    return undefined;
  }

  try {
    return JSON.parse(raw) as API.CurrentUser;
  } catch (_error) {
    return undefined;
  }
};

export const setAdminFallbackUser = (user?: API.CurrentUser) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!user) {
    window.localStorage.removeItem(ADMIN_FALLBACK_USER_KEY);
    return;
  }

  window.localStorage.setItem(ADMIN_FALLBACK_USER_KEY, JSON.stringify(user));
};

export const clearAdminToken = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(ADMIN_AUTH_TOKEN_KEY);
  window.localStorage.removeItem(ADMIN_FALLBACK_USER_KEY);
};

export { ADMIN_AUTH_TOKEN_KEY, ADMIN_FALLBACK_USER_KEY };
